const libFableServiceProviderBase = require('fable-serviceproviderbase');
const libAsync = require('async');
const libGUIDMap = require('./Headlight-Integration-GUIDMap.js');
const libGUIDBatchResolver = require('./Headlight-Integration-GUIDBatchResolver.js');

// Conservative default used until loadSchema() applies the authoritative
// per-entity GUID column size from the live Headlight API.
const _GUID_MAX_LENGTH_FALLBACK = 36;

// Composite-key separator for the unresolved-reference set. Printable on purpose: a literal NUL
// here makes GNU grep treat this whole source file as binary and silently skip it.
const UNRESOLVED_GUID_KEY_SEPARATOR = '\u241F';

/**
 * Is a source record's `Deleted` flag set, in whatever shape the source produced it?
 *
 * Storage layers disagree about how a boolean is represented and every one of them is legitimate:
 * MySQL's `tinyint` arrives as `1`/`0` through mysql2, PostgreSQL gives a real `true`/`false`, and
 * anything that has been through JSON, a CSV, a spreadsheet or a comprehension projection arrives
 * as the string `"1"`. A strict `=== true` honoured only the second of those and treated the rest as
 * a live record — an upsert instead of a delete, reported as success.
 *
 * Only the SET side is widened. `0`, `"0"`, `""`, `false`, `"false"`, `null`, `undefined` and an
 * absent property all remain not-deleted, so a record can never become a delete by accident.
 *
 * Kept in step with the same predicate in `meadow-integration`, which this adapter is a fork of.
 *
 * @param {any} pValue - the source record's `Deleted` value, in any representation
 * @return {boolean} true when the record should be routed to the delete pass
 */
const isDeletedFlagSet = function(pValue)
{
	if (typeof (pValue) === 'boolean')
	{
		return pValue;
	}
	if (typeof (pValue) === 'number')
	{
		return (pValue !== 0);
	}
	if (typeof (pValue) === 'string')
	{
		const tmpValue = pValue.trim().toLowerCase();
		return (tmpValue === '1') || (tmpValue === 'true') || (tmpValue === 't') || (tmpValue === 'y') || (tmpValue === 'yes');
	}
	return false;
};

const defaultHeadlightIntegrationAdapterOptions = (
	{
		"Entity": "DefaultEntity",

		"AdapterSetGUIDMarshalPrefix": false,

		"EntityGUIDMarshalPrefix": false,

		// Maximum allowed length for generated Headlight GUIDs.
		// When 0, the authoritative per-entity size is applied by
		// loadSchema() (called automatically from integrateRecords()).
		// Until then, _GUID_MAX_LENGTH_FALLBACK (36) is used as a
		// conservative default.  Set to a positive integer to explicitly
		// override for all entities.
		"GUIDMaxLength": 0,

		// Optional pre-fetched entity schema document (same shape as the
		// /{Entity}/Schema API response).  When provided, the adapter
		// applies it at construction time and loadSchema() skips the
		// network round-trip.  Useful for callers that cache schemas
		// across many adapters.  Caller is responsible for keeping the
		// cached copy reasonably fresh relative to the deployed server.
		"Schema": null,

		// When false (default), the adapter will throw an error if a generated GUID
		// exceeds GUIDMaxLength, halting the integration.
		// When true, the prefix is truncated to fit while preserving the full external GUID.
		// Only enable this for one-time imports where GUID stability is not required.
		"AllowGUIDTruncation": false,

		"PerformUpserts": true,
		"PerformDeletes": true,

		// Resolve every mapped GUID reference in the source buffer with batched
		// IN-list reads before marshaling, instead of one lookup per reference.
		"BatchGUIDResolution": true,

		// Options for the batch resolver (batch sizes, read mode, whether the
		// body-driven read route may be used) -- see
		// Headlight-Integration-GUIDBatchResolver.js.
		"GUIDBatchResolutionOptions": {},

		"RecordPushRetryThreshold": 5,

		"RecordThresholdForBulkUpsert": 250,
		"BulkUpsertBatchSize": 100,

		"HeadlightAPIURL": '/1.0/'
	});

/**
 * Pull a readable message out of the per-record `Error` property meadow-endpoints attaches to a
 * row that failed inside a bulk operation.
 *
 * The shape depends on the server's meadow-endpoints major -- 2.x sets a preformatted string,
 * 4.x an error object -- so anything truthy counts as a failure and is reduced to a string.
 *
 * @param {any} pRecord - a record row from a bulk response
 * @return {string|false} the failure message, or false when the row carries no error
 */
const getRecordErrorMessage = function(pRecord)
{
	if (!pRecord || (typeof(pRecord) !== 'object') || !pRecord.Error)
	{
		return false;
	}

	const tmpError = pRecord.Error;

	if (typeof(tmpError) === 'string')
	{
		return tmpError;
	}
	if (typeof(tmpError.message) === 'string')
	{
		return tmpError.message;
	}
	if (typeof(tmpError.Error) === 'string')
	{
		return tmpError.Error;
	}

	try
	{
		return JSON.stringify(tmpError);
	}
	catch (pStringifyError)
	{
		return String(tmpError);
	}
};

class HeadlightIntegrationAdapter extends libFableServiceProviderBase
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, defaultHeadlightIntegrationAdapterOptions, pOptions);
		super(pFable, tmpOptions, pServiceHash);

		/** @type {import('fable') & { HeadlightRestClient: import('headlight-rest-client'), HeadlightGUIDMap: libGUIDMap, ProgressTrackerSet: any }} */
		this.fable;
		this.serviceType = 'IntegrationAdapter';

		// Check if there is a GUIDMap .. if not make one
		if (!this.fable.hasOwnProperty('HeadlightGUIDMap'))
		{
			this.fable.addAndInstantiateServiceType('HeadlightGUIDMap', libGUIDMap);
		}

		/** @type {import('headlight-rest-client')} */
		this.client = this.options.Client || this.fable.HeadlightRestClient;
		// Check if there is a REST client ... if not make one
		if (!this.client)
		{
			this.fable.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));
			if (!this.options.hasOwnProperty('HeadlightAPIURL'))
			{
				this.options.HeadlightAPIURL = '/1.0/';
			}
			this.client = this.fable.instantiateServiceProviderWithoutRegistration('HeadlightRestClient', { ServerURL: this.options.HeadlightAPIURL });
		}
		this.client.prepareRequestOptions = (pOptions) =>
		{
			pOptions.timeout = 600000;
			return pOptions;
		};

		this.fable.instantiateServiceProviderIfNotExists('ProgressTrackerSet');

		this.Entity = this.options.Entity;
		this.EntityGUIDName = `GUID${this.Entity}`;
		this.EntityIDName = `ID${this.Entity}`;

		// Automagic GUID Components
		this.AdapterSetGUIDMarshalPrefix = this.options.AdapterSetGUIDMarshalPrefix;
		if (typeof this.AdapterSetGUIDMarshalPrefix !== 'string')
		{
			if (typeof this.fable.settings.AdapterSetGUIDMarshalPrefix === 'string')
			{
				this.AdapterSetGUIDMarshalPrefix = this.fable.settings.AdapterSetGUIDMarshalPrefix;
			}
			else
			{
				this.AdapterSetGUIDMarshalPrefix = 'INTG-DEF';
			}
		}
		this.EntityGUIDMarshalPrefix = this.options.EntityGUIDMarshalPrefix;
		if (typeof this.EntityGUIDMarshalPrefix !== 'string')
		{
			this.EntityGUIDMarshalPrefix = `E-${this.Entity}`;
		}

		// Resolve GUID max length: explicit option > fallback.  The
		// authoritative per-entity size is applied by loadSchema() once
		// the live server schema has been fetched.
		if (this.options.GUIDMaxLength > 0)
		{
			this.GUIDMaxLength = this.options.GUIDMaxLength;
		}
		else
		{
			this.GUIDMaxLength = _GUID_MAX_LENGTH_FALLBACK;
		}
		this.AllowGUIDTruncation = this.options.AllowGUIDTruncation;

		// Integration Adapter Controls
		this._PerformUpserts = this.options.PerformUpserts;
		this._PerformDeletes = this.options.PerformDeletes;

		this._RecordPushRetryThreshold = this.options.RecordPushRetryThreshold;

		// Meta progress tracker — an optional external progress tracker hash that is
		// incremented alongside the per-entity tracker so callers can monitor overall
		// progress across multiple adapters/entities.
		this.MetaProgressTrackerHash = false;
		// How often (in records) to log the meta progress tracker status (0 = never auto-log).
		this.MetaProgressTrackerLogInterval = 0;

		// The source records (coming from the external system)
		this._SourceRecords = {};

		// The marshaled records (going to HeadLight or Queued for Delete)
		this._MarshaledRecords = {};
		this._DeletedRecords = {};

		// Records the server refused, one entry per record, for the run summary.
		/** @type {Array<{ Entity: string, RecordKey: string, Error: string }>} */
		this.RecordPushFailures = [];

		/** @type {import('./Headlight-Integration-GUIDBatchResolver.js')} */
		this._GUIDBatchResolver = null;

		// Mapped GUIDs the server has already been asked about -- and did not
		// have -- during the current marshal pass.  Without this, a reference
		// that resolves to nothing is re-requested once per record that carries
		// it.  Scoped to the pass because a GUID absent now can be created by a
		// later stage of the same run.
		/** @type {Set<string>} */
		this._UnresolvedMappedGUIDs = new Set();

		// Whether this server serves the body-driven read route, per the
		// capabilities its schema advertises.
		/** @type {boolean|undefined} */
		this.QueryEndpointSupported = undefined;

		// Optional caller-supplied schema: stash it and apply the authoritative
		// GUID size now so consumers can generate GUIDs without first awaiting
		// a network round-trip.
		if (this.options.Schema)
		{
			this._applySchemaDocument(this.options.Schema);
		}
	}

	/**
	 * Apply an entity schema document (in the shape returned by the
	 * /{Entity}/Schema API) to this adapter: stash it on meadowSchema and,
	 * unless the caller explicitly set GUIDMaxLength in options, adopt the
	 * GUID column size from the schema's `properties` map.
	 *
	 * @param {Object} pSchemaDocument - The entity schema document
	 * @private
	 */
	_applySchemaDocument(pSchemaDocument)
	{
		if (!pSchemaDocument || typeof(pSchemaDocument) !== 'object')
		{
			return;
		}
		this.meadowSchema = pSchemaDocument;

		// The advertised capability describes the server, not the entity, so it
		// applies to every entity this adapter resolves references against.
		const tmpCapabilities = pSchemaDocument.RetoldMetadata && pSchemaDocument.RetoldMetadata.Capabilities;
		if (tmpCapabilities && typeof(tmpCapabilities.QueryEndpoint) === 'boolean')
		{
			this.QueryEndpointSupported = tmpCapabilities.QueryEndpoint;
			if (this._GUIDBatchResolver)
			{
				this._GUIDBatchResolver.QueryEndpointSupported = tmpCapabilities.QueryEndpoint;
			}
		}

		if (this.options.GUIDMaxLength <= 0
			&& pSchemaDocument.properties
			&& pSchemaDocument.properties[this.EntityGUIDName])
		{
			let tmpSchemaSize = Number(pSchemaDocument.properties[this.EntityGUIDName].size);
			if (tmpSchemaSize > 0)
			{
				if (tmpSchemaSize !== this.GUIDMaxLength)
				{
					this.log.info(`Schema GUID column size for [${this.Entity}] is ${tmpSchemaSize} (was ${this.GUIDMaxLength}); using schema value.`);
				}
				this.GUIDMaxLength = tmpSchemaSize;
			}
		}
	}

	/**
	 * Load the live entity schema from the Headlight API and apply the
	 * authoritative GUID column size when no explicit GUIDMaxLength override
	 * was provided.  integrateRecords() calls this automatically; consumers
	 * that need to generate GUIDs before integration runs should call it
	 * explicitly.  Idempotent: if a schema has already been applied (via the
	 * Schema option or a prior call), the network fetch is skipped.
	 *
	 * @param {(error?: Error) => void} fCallback - Callback fired when the schema has been loaded (or failed to load)
	 */
	loadSchema(fCallback)
	{
		if (this.meadowSchema && typeof(this.meadowSchema) === 'object')
		{
			return fCallback();
		}
		this.fable.log.info(`Getting schema for ${this.Entity}....`);
		let tmpRequestOptions = (
			{
				url: `${this.client.serverURL}${this.Entity}/Schema`
			});
		tmpRequestOptions = this.client._prepareRequestOptions(tmpRequestOptions);
		return this.client.restClient.getJSON(tmpRequestOptions,
			(pError, pResponse, pBody) =>
			{
				if (!pError)
				{
					this._applySchemaDocument(pBody);
				}
				return fCallback(pError);
			});
	}

	/**
	 * TODO: This was lifted from the old HDOT integration code but a more elegant streaming solution would be way cooler.
	 *
	 * @param {(error?: Error) => void} fCallback - Callback function to call when the records are integrated
	 * @param {(source: any, marshalled: any) => void} [fMarshalExtraData] - (optional) Function to call to marshal extra data per-record
	 */
	integrateRecords(fCallback, fMarshalExtraData)
	{
		let tmpMarshalExtraData = fMarshalExtraData;
		let tmpAnticipate = this.fable.newAnticipate();
		if (this.options.ValidateSession)
		{
			tmpAnticipate.anticipate(
				(fStageComplete)=>
				{
					this.client.getJSON('CheckSession', (pError, pBody) =>
						{
							if (pError)
							{
								this.fable.log.error(`Error checking session with Headlight server: ${pError.message || pError}`, { Stack: pError.stack });
								return fStageComplete(pError);
							}
							if (!pBody || !pBody.LoggedIn)
							{
								this.fable.log.error(`Not logged in; cannot proceed with integration of ${this.Entity} records.`, { Response: pBody });
								return fStageComplete(new Error('Not logged in'));
							}
							return fStageComplete();
						});
				});
		}
		tmpAnticipate.anticipate(
			(fStageComplete)=>
			{
				this.loadSchema(fStageComplete);
			});
		tmpAnticipate.anticipate(
			(fStageComplete)=>
			{
				this.fable.log.info(`Marshaling ${this.Entity} records....`);
				this.marshalSourceRecords(fStageComplete, tmpMarshalExtraData);
			});
		tmpAnticipate.anticipate(
			(fStageComplete)=>
			{
				this.fable.log.info(`Posting ${this.Entity} records....`);
				this.pushRecordsToServer(fStageComplete);
			});
		// Retirements are routed to _DeletedRecords by marshalSingleSourceRecord, and
		// pushRecordsToServer only walks _MarshaledRecords -- so without this stage the delete queue
		// was built and then dropped when the adapter went out of scope. A record flagged Deleted
		// therefore neither upserted nor deleted, and the run reported success either way. Guarded
		// inside deleteRecordsFromServer by PerformDeletes, and a no-op when nothing is queued.
		tmpAnticipate.anticipate(
			(fStageComplete)=>
			{
				const tmpDeleteCount = Object.keys(this._DeletedRecords).length;
				if (tmpDeleteCount < 1)
				{
					return fStageComplete();
				}
				this.fable.log.info(`Deleting ${tmpDeleteCount} ${this.Entity} record(s)....`);
				this.deleteRecordsFromServer(fStageComplete);
			});
		tmpAnticipate.wait(fCallback);
	}

	/**
	 * Add a record to the adapter's Source Records buffer to be pushed
	 *
	 * @param {Object} pRecord - The record to add
	 */
	addSourceRecord(pRecord)
	{
		if (!pRecord || typeof(pRecord) !== 'object' || Array.isArray(pRecord))
		{
			this.log.error(`Passed-in record was null, or not of type "object" (${typeof(pRecord)}), therefore it was not added to the Source Record buffer.`);
			return false;
		}
		let tmpRecordGUID = pRecord[this.EntityGUIDName];
		if (!tmpRecordGUID && (!pRecord.hasOwnProperty(this.EntityIDName) || !(pRecord[this.EntityIDName])))
		{
			tmpRecordGUID = pRecord[`_${this.EntityGUIDName}`];
			if (!tmpRecordGUID)
			{
				this.log.error(`Passed-in record did not contain a source system GUID data element [${this.Entity}].[${this.EntityGUIDName}], therefore it was not added to the Source Record Buffer:`, pRecord);
				return false;
			}
		}

		if (tmpRecordGUID)
		{
			this._SourceRecords[tmpRecordGUID] = pRecord;
		}
		else
		{
			this._SourceRecords[pRecord[this.EntityIDName]] = pRecord;
		}
	}

	get GUIDPrefix()
	{
		let tmpPrefix = '';
		if (this.AdapterSetGUIDMarshalPrefix)
		{
			tmpPrefix += `${this.AdapterSetGUIDMarshalPrefix}-`;
		}
		if (this.EntityGUIDMarshalPrefix)
		{
			tmpPrefix += `${this.EntityGUIDMarshalPrefix}-`;
		}
		return tmpPrefix;
	}

	/**
	 * Generate a Headlight GUID from an external system GUID
	 *
	 * If the generated GUID would exceed GUIDMaxLength (resolved per-entity
	 * from the Headlight schema) and AllowGUIDTruncation is false (the
	 * default), an error is thrown so the integration stops immediately with
	 * a clear message.
	 *
	 * When AllowGUIDTruncation is true (one-time imports only), the prefix is
	 * progressively truncated to fit while preserving the full external GUID.
	 *
	 * @param {string} pExternalGUID - The external system GUID to generate a Headlight GUID from
	 * @return {string} The generated Headlight GUID
	 */
	generateHeadlightGUIDFromExternalGUID(pExternalGUID)
	{
		let tmpFullGUID = `${this.GUIDPrefix}${pExternalGUID}`;

		if (this.GUIDMaxLength > 0 && tmpFullGUID.length > this.GUIDMaxLength)
		{
			if (!this.AllowGUIDTruncation)
			{
				let tmpMessage = `Generated GUID for [${this.Entity}] exceeds the maximum allowed length of ${this.GUIDMaxLength} characters.\n`
					+ `  Comprehension GUID: [${pExternalGUID}] (${pExternalGUID.length} chars)\n`
					+ `  Server GUID:        [${tmpFullGUID}] (${tmpFullGUID.length} chars)\n`
					+ `  Prefix:             [${this.GUIDPrefix}] (${this.GUIDPrefix.length} chars)\n`
					+ `  To allow automatic prefix truncation for one-time imports, set AllowGUIDTruncation to true (CLI: --allowguidtruncation).`;
				this.log.error(tmpMessage);
				throw new Error(tmpMessage);
			}

			// AllowGUIDTruncation is on -- the external GUID is sacrosanct, so truncate the prefix instead.
			let tmpAvailablePrefixLength = this.GUIDMaxLength - pExternalGUID.length;

			if (tmpAvailablePrefixLength <= 0)
			{
				// External GUID alone meets or exceeds the limit; drop the prefix entirely.
				this.log.warn(`External GUID [${pExternalGUID}] for [${this.Entity}] is ${pExternalGUID.length} characters which meets or exceeds the GUID max length of ${this.GUIDMaxLength}.  Using external GUID with no prefix.`);
				tmpFullGUID = pExternalGUID.substring(0, this.GUIDMaxLength);
			}
			else
			{
				let tmpTruncatedPrefix = this.GUIDPrefix.substring(0, tmpAvailablePrefixLength);
				tmpFullGUID = `${tmpTruncatedPrefix}${pExternalGUID}`;
				this.log.warn(`Generated GUID for [${this.Entity}] would be ${this.GUIDPrefix.length + pExternalGUID.length} characters (limit ${this.GUIDMaxLength}); prefix truncated from [${this.GUIDPrefix}] to [${tmpTruncatedPrefix}].`);
			}
		}

		return tmpFullGUID;
	}

	/**
	 * The batch resolver used to pre-populate the GUID map before marshaling.
	 *
	 * @return {import('./Headlight-Integration-GUIDBatchResolver.js')}
	 */
	get guidBatchResolver()
	{
		if (!this._GUIDBatchResolver)
		{
			this.fable.addServiceType('IntegrationGUIDBatchResolver', libGUIDBatchResolver);
			const tmpOptions = Object.assign({}, this.options.GUIDBatchResolutionOptions, { QueryEndpointSupported: this.QueryEndpointSupported });
			this._GUIDBatchResolver = this.fable.instantiateServiceProviderWithoutRegistration('IntegrationGUIDBatchResolver', tmpOptions);
		}
		this._GUIDBatchResolver.client = this.client;
		return this._GUIDBatchResolver;
	}

	/**
	 * @param {string} pMappedEntityName - The referenced entity.
	 * @param {string} pMappedGUIDValue - The referenced GUID.
	 * @return {string} The key this pass tracks an unresolvable reference under.
	 * @private
	 */
	_unresolvedMappedGUIDKey(pMappedEntityName, pMappedGUIDValue)
	{
		return `${pMappedEntityName}${UNRESOLVED_GUID_KEY_SEPARATOR}${pMappedGUIDValue}`;
	}

	/**
	 * Resolve every mapped GUID reference in the source buffer up front, in
	 * batched IN-list reads, so the marshal path finds them in the GUID map.
	 *
	 * References already answered by the in-memory maps -- entities pushed
	 * earlier in this run, GUIDs resolved by an earlier batch -- are never
	 * re-requested.  Failures are not fatal: anything a batch does not resolve
	 * still goes through the single-GUID lookup during marshaling.
	 *
	 * @return {Promise<Object|null>} The resolver's summary, or null when nothing was prefetched.
	 */
	async prefetchMappedEntityIDs()
	{
		if (!this.options.BatchGUIDResolution)
		{
			return null;
		}

		try
		{
			const tmpReferences = libGUIDBatchResolver.collectMappedGUIDReferences(this._SourceRecords, this.EntityGUIDName);
			if (tmpReferences.size < 1)
			{
				return null;
			}

			const tmpSummary = await this.guidBatchResolver.resolveReferences(tmpReferences);

			for (const [tmpMappedEntityName, tmpMissingGUIDs] of tmpSummary.Missing)
			{
				for (const tmpMissingGUID of tmpMissingGUIDs)
				{
					this._UnresolvedMappedGUIDs.add(this._unresolvedMappedGUIDKey(tmpMappedEntityName, tmpMissingGUID));
				}
			}

			if (tmpSummary.Requested > 0)
			{
				const tmpUnverifiedNote = (tmpSummary.Unverified > 0) ? `, ${tmpSummary.Unverified} left to single lookups` : '';
				this.fable.log.info(`Resolved ${tmpSummary.Resolved} of ${tmpSummary.Requested} referenced GUID(s) for [${this.Entity}] in ${tmpSummary.Requests} batch read(s) (${tmpSummary.Cached} already cached${tmpUnverifiedNote}).`);
			}
			return tmpSummary;
		}
		catch (pError)
		{
			this.fable.log.warn(`Batch GUID resolution for [${this.Entity}] did not complete; falling back to per-reference lookups: ${pError.message || pError}`, { Stack: pError.stack });
			return null;
		}
	}

	/**
	 * Resolve a referenced entity's GUID to its Headlight ID, consistently for
	 * every mapped-GUID form (`GUID<Entity>`, `_GUID<Entity>`, and
	 * `_Dest_IDEntity_..._Via_GUID<Entity>`).
	 *
	 * Resolution order:
	 *   1. This run's in-memory external map (external GUID -> Headlight GUID ->
	 *      ID), populated when the referenced entity was integrated earlier in
	 *      the same run.
	 *   2. A direct GUID lookup that checks the in-memory map -- which the batch
	 *      prefetch has usually already filled -- and then falls back to a live
	 *      server fetch.  This second step is what lets a reference resolve when
	 *      its target was loaded by a *prior* run (and so is absent from this
	 *      run's cache) -- the case the `GUID<Entity>` branch used to miss,
	 *      dropping the foreign key.
	 *
	 * @param {string} pMappedEntityName - The referenced entity (e.g. 'PayItem')
	 * @param {string} pMappedGUIDValue - The referenced GUID (external or Headlight)
	 * @return {Promise<number>} The resolved Headlight ID, or 0 if none could be found
	 */
	async resolveMappedEntityID(pMappedEntityName, pMappedGUIDValue)
	{
		if (!pMappedGUIDValue)
		{
			return 0;
		}

		// 1. External-GUID cache (external GUID -> Headlight GUID -> ID).
		let tmpHeadlightIDValue = this.fable.HeadlightGUIDMap.getHeadlightIDFromExternalGUID(pMappedEntityName, pMappedGUIDValue);
		if (tmpHeadlightIDValue)
		{
			return tmpHeadlightIDValue;
		}

		const tmpUnresolvedKey = this._unresolvedMappedGUIDKey(pMappedEntityName, pMappedGUIDValue);
		if (this._UnresolvedMappedGUIDs.has(tmpUnresolvedKey))
		{
			return 0;
		}

		// 2. Direct GUID lookup: in-memory map, then live server fetch.  Covers
		//    GUIDs that are already Headlight GUIDs, were stored without a prefix,
		//    or belong to an entity integrated by a prior run (not in this run's
		//    cache).
		return await new Promise((resolve) =>
		{
			this.fable.HeadlightGUIDMap.getIDFromGUIDAsync(pMappedEntityName, pMappedGUIDValue,
			(pError, pResponse) =>
			{
				if (pResponse)
				{
					return resolve(pResponse);
				}
				if (pError)
				{
					this.fable.log.warn(`Error getting Headlight id for ${pMappedEntityName} GUID [${pMappedGUIDValue}]: ${pError.message || pError}`, { Stack: pError.stack });
				}
				this._UnresolvedMappedGUIDs.add(tmpUnresolvedKey);
				return resolve(0);
			}, this.client);
		});
	}

	/**
	 * Marshal a record from the source system to a Headlight record
	 *
	 * @param {Object} pSourceRecord - The source record to marshal
	 * @return {Promise<Object>} The marshaled record
	 */
	async marshalRecord(pSourceRecord)
	{
		let tmpRecord = {};

		// Create the new GUID
		let tmpRecordHeadLightGUID;
		let tmpRecordExternalGUID = pSourceRecord[this.EntityGUIDName];
		if (tmpRecordExternalGUID)
		{
			tmpRecordHeadLightGUID = this.generateHeadlightGUIDFromExternalGUID(tmpRecordExternalGUID);
		}
		else
		{
			tmpRecordExternalGUID = pSourceRecord[`_${this.EntityGUIDName}`];
			tmpRecordHeadLightGUID = tmpRecordExternalGUID;
		}

		if (!tmpRecordHeadLightGUID && !pSourceRecord.hasOwnProperty(this.EntityIDName))
		{
			throw new Error(`Could not marshal record for [${this.Entity}] because no external system GUID was found in source record.`);
		}

		if (tmpRecordHeadLightGUID)
		{
			// Mapping table for going between HeadLight system GUIDs and External system GUIDs.
			tmpRecord[this.EntityGUIDName] = tmpRecordHeadLightGUID;
			this.fable.HeadlightGUIDMap.mapExternalGUIDtoHeadlightGUID(this.Entity, tmpRecordExternalGUID, tmpRecordHeadLightGUID);
		}

		// Now that we've dealt with basic identifiers, time to see if there are other Mapped GUIDs to look up.
		// TODO: This can be the path through to a recursive integration -- if
		//       the GUIDs are not found, then the appropriate adapter can launch
		//       (and integrate just the proper records!)
		for (const tmpRecordKey of Object.keys(pSourceRecord))
		{
			const tmpMappedReference = libGUIDBatchResolver.classifyMappedGUIDField(tmpRecordKey, this.EntityGUIDName);

			if (tmpMappedReference)
			{
				const tmpMappedEntityName = tmpMappedReference.MappedEntityName;
				const tmpMappedEntityGUIDValue = pSourceRecord[tmpRecordKey];

				let tmpHeadlightIDValue = await this.resolveMappedEntityID(tmpMappedEntityName, tmpMappedEntityGUIDValue);
				if (tmpHeadlightIDValue)
				{
					const tmpIDDestinationField = tmpMappedReference.DestinationField || pSourceRecord[`_Dest_${tmpRecordKey}`] || `ID${tmpMappedEntityName}`;
					tmpRecord[tmpIDDestinationField] = tmpHeadlightIDValue;
					if (tmpMappedReference.SetMappedEntityIDField)
					{
						tmpRecord[`ID${tmpMappedEntityName}`] = tmpHeadlightIDValue;
					}
				}
				else
				{
					const tmpGUIDLabel = (tmpMappedReference.Kind === 'External') ? 'External GUID' : 'GUID';
					this.fable.log.warn(`Could not find Headlight ID for [${tmpMappedEntityName}] with ${tmpGUIDLabel} [${tmpMappedEntityGUIDValue}] while integrating [${this.Entity}] record [${tmpRecord[this.EntityGUIDName]}].`)
				}
			}
			else if (tmpRecordKey == this.EntityGUIDName || tmpRecordKey == `_${this.EntityGUIDName}`)
			{
				// Don't do anything for the GUID whose already set...
			}
			else if (tmpRecordKey.startsWith('_Dest_'))
			{
				// skip this, it's a destination field override for a GUID
			}
			else if ((this.meadowSchema && this.meadowSchema.hasOwnProperty('properties')) && (this.meadowSchema.properties.hasOwnProperty(tmpRecordKey)))
			{
				// Check the length if it's a string -- truncate if it isn't there for now.
				// TODO: MAKE THIS CONFIGURABLE AND OVERRIDABLE
				if (this.options.SimpleMarshal)
				{
					//FIXME: this is a hack I added to allow me to marshal object types without things breaking; revisit - bigal
					tmpRecord[tmpRecordKey] = pSourceRecord[tmpRecordKey];
				}
				else if ((this.meadowSchema.properties[tmpRecordKey].type == 'string')
					&& pSourceRecord.hasOwnProperty(tmpRecordKey)
					&& (pSourceRecord[tmpRecordKey] != null)
					&& (pSourceRecord[tmpRecordKey].toString().length > this.meadowSchema.properties[tmpRecordKey].size))
				{
					tmpRecord[tmpRecordKey] = pSourceRecord[tmpRecordKey].substring(0, this.meadowSchema.properties[tmpRecordKey].size);
				}
				else if (this.meadowSchema.properties[tmpRecordKey].type == 'string')
				{
					if ((pSourceRecord[tmpRecordKey] !== null) && (pSourceRecord[tmpRecordKey] !== undefined))
					{
						tmpRecord[tmpRecordKey] = pSourceRecord[tmpRecordKey].toString();
					}
				}
				else
				{
					tmpRecord[tmpRecordKey] = pSourceRecord[tmpRecordKey];
				}
			}
			else if (this.options.ForceMarshal)
			{
				tmpRecord[tmpRecordKey] = pSourceRecord[tmpRecordKey];
			}
		}

		// Clean any elements in the record that are reserved by HeadLight
		if (tmpRecord.hasOwnProperty('CreateDate')) delete tmpRecord.CreateDate;
		if (tmpRecord.hasOwnProperty('UpdateDate')) delete tmpRecord.UpdateDate;
		if (tmpRecord.hasOwnProperty('Deleted')) delete tmpRecord.Deleted;

		return tmpRecord;
	}

	/**
	 * Marshal a single source record
	 *
	 * @param {string} pSourceRecordGUID - The GUID of the source record to marshal
	 * @param {(source: any, marshalled: any) => void} [fMarshalExtraData] - (optional) Function to call to marshal extra data per-record
	 *
	 * @return {Promise<boolean>} - Promise that resolves to true (why?)
	 */
	async marshalSingleSourceRecord(pSourceRecordGUID, fMarshalExtraData)
	{
		// 0. Get the original GUID
		let tmpSourceRecordGUID = pSourceRecordGUID;

		// 0.3 Get the Source Record
		let tmpSourceRecord = this._SourceRecords[tmpSourceRecordGUID];

		if (!tmpSourceRecord)
		{
			this.log.fatal(`Could not marshal source record for [${this.Entity}] because source record with GUID [${tmpSourceRecordGUID}] was not found in Source Records buffer.`);
			return;
		}

		// 0.5 Check if this is a delete
		let tmpDeleteOperation = isDeletedFlagSet(tmpSourceRecord.Deleted);

		// 1. Marshal the Source record into a HeadLight record
		let tmpMarshaledRecord = await this.marshalRecord(tmpSourceRecord);

		// 2. Get the GUID of the record after Marshaling...
		let tmpMarshaledRecordGUID = tmpMarshaledRecord[this.EntityIDName];
		if (tmpMarshaledRecord.hasOwnProperty(this.EntityGUIDName))
		{
			tmpMarshaledRecordGUID = tmpMarshaledRecord[this.EntityGUIDName];
		}
		else if (tmpMarshaledRecord.hasOwnProperty(`_${this.EntityGUIDName}`))
		{
			tmpMarshaledRecordGUID = tmpMarshaledRecord[`_${this.EntityGUIDName}`];
		}

		// 3. Get a new Object or the existing object as start of the append operation
		let tmpOriginalRecord = (this._MarshaledRecords.hasOwnProperty(tmpMarshaledRecordGUID)) ? this._MarshaledRecords[tmpMarshaledRecordGUID] : {};

		// 4. Now merge the properties of the two records, using the new one as the more important values
		//    Yes this will guaranteed overwrite the GUID like a zillion times, but, whatevers
		if (tmpDeleteOperation)
		{
			this._DeletedRecords[tmpMarshaledRecordGUID] = tmpMarshaledRecord;
		}
		else
		{
			this._MarshaledRecords[tmpMarshaledRecordGUID] = Object.assign(tmpOriginalRecord, tmpMarshaledRecord);
		}

		// 4.5 Inject a marshal stack
		if (typeof(fMarshalExtraData) == 'function')
		{
			fMarshalExtraData(tmpSourceRecord, tmpMarshaledRecord);
		}

		// 5. Now delete the record from the SourceRecords set so next marshal operation doesn't do it again needlessly
		delete this._SourceRecords[tmpSourceRecordGUID];

		return true;
	}

	/**
	 * Take all Source Records in the buffer and marshal them
	 *
	 * @param {(error?: Error) => void} fCallback - Callback function to call when the records are marshaled
	 * @param {(source: any, marshalled: any) => void} [fMarshalExtraData] - (optional) Function to call to marshal extra data per-record
	 * @return {Promise<void>} - Promise that resolves when all records are marshaled
	 */
	async marshalSourceRecords(fCallback, fMarshalExtraData)
	{
		try
		{
			this._UnresolvedMappedGUIDs.clear();
			await this.prefetchMappedEntityIDs();

			let tmpRecordKeys = Object.keys(this._SourceRecords);

			// Later switch to something paralleler?
			for (let i = 0; i < tmpRecordKeys.length; i++)
			{
				await this.marshalSingleSourceRecord(tmpRecordKeys[i], fMarshalExtraData);
			}

			return fCallback();
		}
		catch (error)
		{
			return fCallback(error);
		}
	}

	/**
	 * Log a record the server refused and tally it for the run summary.
	 *
	 * These are decisions the server has already made about one record -- a constraint violation,
	 * a value too long for its column -- so they are terminal for that record and never retried.
	 * The rest of the batch is unaffected and the load continues.
	 *
	 * @param {string} pRecordKey - the GUID (or ID) the marshaled record is keyed on
	 * @param {string} pMessage - the failure as reported by the server
	 */
	trackRecordPushFailure(pRecordKey, pMessage)
	{
		this.RecordPushFailures.push({ Entity: this.Entity, RecordKey: pRecordKey, Error: pMessage });
		this.log.error(`Record push failure for [${this.Entity}].[${pRecordKey}]: ${pMessage}`);
	}

	/**
	 * Match a bulk upsert response back to the batch that was sent: map the records that landed,
	 * and log the ones that did not.
	 *
	 * A bulk upsert answers 200 whether or not every row succeeded, and the two shapes of
	 * per-record failure both arrive inside that 200. meadow-endpoints 2.x returns the failed row
	 * in the array with an `Error` property set; 4.x omits it from the array entirely and reports
	 * the counts in `X-Meadow-Upsert-*` response headers, which the REST client does not surface.
	 * Trusting every returned row therefore writes a GUID -> undefined entry into the GUID map,
	 * which every later reference to that record then resolves against.
	 *
	 * @param {string[]} pRecordGUIDs - the record keys sent in this batch
	 * @param {Array<Object>} pResponseRecords - the array the server answered with
	 * @return {number} how many records in the batch did not land
	 */
	reconcileBulkUpsertResponse(pRecordGUIDs, pResponseRecords)
	{
		let tmpResponseByKey = {};

		for (let i = 0; i < pResponseRecords.length; i++)
		{
			let tmpResponseRecord = pResponseRecords[i];

			if (!tmpResponseRecord || (typeof(tmpResponseRecord) !== 'object'))
			{
				continue;
			}
			if (tmpResponseRecord[this.EntityGUIDName])
			{
				tmpResponseByKey[tmpResponseRecord[this.EntityGUIDName]] = tmpResponseRecord;
			}
			if (tmpResponseRecord[this.EntityIDName])
			{
				tmpResponseByKey[tmpResponseRecord[this.EntityIDName]] = tmpResponseRecord;
			}
		}

		let tmpFailureCount = 0;

		for (let i = 0; i < pRecordGUIDs.length; i++)
		{
			let tmpRecordGUID = pRecordGUIDs[i];
			let tmpResponseRecord = tmpResponseByKey[tmpRecordGUID];

			if (!tmpResponseRecord)
			{
				tmpFailureCount++;
				this.trackRecordPushFailure(tmpRecordGUID, `The server did not return a row for this record in the bulk upsert response.`);
				continue;
			}

			let tmpErrorMessage = getRecordErrorMessage(tmpResponseRecord);
			if (tmpErrorMessage)
			{
				tmpFailureCount++;
				this.trackRecordPushFailure(tmpRecordGUID, tmpErrorMessage);
				continue;
			}

			if (!(tmpResponseRecord[this.EntityIDName] > 0))
			{
				tmpFailureCount++;
				this.trackRecordPushFailure(tmpRecordGUID, `The server returned this record without a valid ${this.EntityIDName}; it was not persisted.`);
				continue;
			}

			let tmpMappedGUID = (tmpResponseRecord[this.EntityGUIDName]) ? tmpResponseRecord[this.EntityGUIDName] : tmpRecordGUID;
			this.fable.HeadlightGUIDMap.mapGUIDToID(this.Entity, tmpMappedGUID, tmpResponseRecord[this.EntityIDName]);
		}

		if (tmpFailureCount > 0)
		{
			this.log.error(`[${this.Entity}] Bulk upsert batch of ${pRecordGUIDs.length} records completed with ${tmpFailureCount} record-level failure(s); these are not retried.`);
		}

		return tmpFailureCount;
	}

	/**
	 * Take a single record and push it to the server
	 *
	 * @param {(error?: Error) => void} fCallback - Callback function to call when the record is pushed
	 * @param {string} pRecordGUID - The GUID of the record to push
	 * @param {number} [pRetryCount] - (optional) The number of times this record has been retried
	 */
	upsertSingleRecord(fCallback, pRecordGUID, pRetryCount)
	{
		// TODO: The retry count recursion method here is very brute force.  Fix it!
		let tmpRetryCount = (typeof(pRetryCount) === 'undefined') ? 0 : pRetryCount;

		// Non-configurable cap for a bit here... until better logic is written.
		if ((tmpRetryCount > this._RecordPushRetryThreshold) || (tmpRetryCount > 50))
		{
			let tmpIdentifierType = 'GUID';
			this.trackRecordPushFailure(pRecordGUID, `Upsert retry threshold of ${this._RecordPushRetryThreshold} reached.`);

			// upsert failed, so we will try to read the record instead so we can at least have a valid ID / GUID mapping
			const tmpFallbackCallback = (pError, pBody) =>
			{
				if (pError)
				{
					this.log.error(`Error reading record ${tmpIdentifierType} [${pRecordGUID}] after upsert failures: ${pError.message || pError}`, { Stack: pError.stack });
				}
				else if (pBody &&
						// Check that the server returned a valid record (look for the Identity column)
						(pBody.hasOwnProperty(this.EntityIDName)) &&
						// Check that the server returned a record that has a numeric ID which is nonzero
						(pBody[this.EntityIDName] > 0) &&
						// Check that the server also returned a GUID
						(pBody.hasOwnProperty(this.EntityGUIDName)) &&
						// Check that the GUID or ID matches what we expect
						((pBody[this.EntityGUIDName] === pRecordGUID) || (pBody[this.EntityIDName] == pRecordGUID))
					)
				{
					this.log.info(`Fallback: Loaded and mapping record ${tmpIdentifierType} [${pRecordGUID}] after upsert failure.`,
						{
							Record: pBody,
							Entity: this.Entity,
							IdentifierType: tmpIdentifierType,
							GUID: pBody[this.EntityGUIDName],
							ID: pBody[this.EntityIDName]
						});
					this.fable.HeadlightGUIDMap.mapGUIDToID(this.Entity, pBody[this.EntityGUIDName], pBody[this.EntityIDName]);
				}
				else
				{
					this.log.error(`Could not verify record ${tmpIdentifierType} [${pRecordGUID}] after upsert failures; record not found or invalid response.`, { Record: pBody });
				}
				return fCallback();
			};
			if (this._MarshaledRecords[pRecordGUID][this.EntityIDName] == pRecordGUID)
			{
				tmpIdentifierType = 'ID';
				// It's an ID, not a GUID
				return this.client.getEntity(this.Entity, pRecordGUID, tmpFallbackCallback);
			}
			return this.client.getEntityByGUID(this.Entity, pRecordGUID, tmpFallbackCallback);
		}

		this.client.upsertEntity(this.Entity, this._MarshaledRecords[pRecordGUID],
			(pError, pBody)=>
			{
				if (pError)
				{
					this.log.error(`Error sending PUT [${this.Entity}].[${pRecordGUID}] to server:  ${pError}`);
					let tmpErrorMessage = (typeof(pError.message) === 'string') ? pError.message : String(pError);
					if (tmpErrorMessage.indexOf('Error in DAL create: Error: Duplicate entry') > 0)
					{
						this.log.warn(`Duplicate record attempted when sending PUT to server record GUID [${pRecordGUID}]: ${tmpErrorMessage}`, pBody);
						return fCallback();
					}
					if (tmpErrorMessage.indexOf('exceeds the maximum allowed length') > 0)
					{
						this.trackRecordPushFailure(pRecordGUID, `GUID length rejected by server (${pRecordGUID.length} chars): ${tmpErrorMessage}`);
						return fCallback();
					}
					if (tmpErrorMessage.indexOf('Rejected Create') > -1 && tmpErrorMessage.indexOf('GUID') > -1)
					{
						this.trackRecordPushFailure(pRecordGUID, `Server rejected create due to a GUID issue: ${tmpErrorMessage}`);
						return fCallback();
					}

					// simple delay for retries to not spam the server
					setTimeout(() =>
					{
						this.upsertSingleRecord(fCallback, pRecordGUID, tmpRetryCount + 1);
					}, 500);
					return;
				}

				if (
						pBody &&
						// Check that the server returned a valid record (look for the Identity column)
						(pBody.hasOwnProperty(this.EntityIDName)) &&
						// Check that the server returned a record that has a numeric ID which is nonzero
						(pBody[this.EntityIDName] > 0) &&
						// Check that the server also returned a GUID
						(pBody.hasOwnProperty(this.EntityGUIDName)) &&
						// Check that the GUID or ID matches what we expect
						((pBody[this.EntityGUIDName] === pRecordGUID) || (pBody[this.EntityIDName] == pRecordGUID))
					)
				{
					// Add the record ID to the lookup table
					this.fable.HeadlightGUIDMap.mapGUIDToID(this.Entity, pBody[this.EntityGUIDName], pBody[this.EntityIDName]);
					return fCallback();
				}

				// Try again...
				this.log.error(`Problem sending PUT [${this.Entity}].[${pRecordGUID}] to server.  Incrementing retry count and trying again.`, { Record: pBody, RetryCount: tmpRetryCount });
				this.upsertSingleRecord(fCallback, pRecordGUID, tmpRetryCount + 1);
			});
	}

	/**
	 * Take a set of records and push them to the server
	 *
	 * @param {(error?: Error) => void} fCallback - Callback function to call when the records are pushed
	 * @param {string[]} pRecordGUIDs - The GUIDs of the records to push
	 * @param {number} [pRetryCount] - (optional) The number of times this record has been retried
	 */
	upsertBulkRecords(fCallback, pRecordGUIDs, pRetryCount)
	{
		// TODO: The retry count recursion method here is very brute force.  Fix it!
		let tmpRetryCount = (typeof(pRetryCount) === 'undefined') ? 0 : pRetryCount;

		// Non-configurable cap for a bit here... until better logic is written.
		if ((tmpRetryCount > this._RecordPushRetryThreshold) || (tmpRetryCount > 50))
		{
			for (let i = 0; i < pRecordGUIDs.length; i++)
			{
				this.trackRecordPushFailure(pRecordGUIDs[i], `Bulk upsert retry threshold of ${this._RecordPushRetryThreshold} reached.`);
			}
			//FIXME: handle reading records in bulk to backfill ID / GUID map?
			return fCallback();
		}

		let tmpRecordsToUpsert = [];

		for (let i = 0; i < pRecordGUIDs.length; i++)
		{
			tmpRecordsToUpsert.push(this._MarshaledRecords[pRecordGUIDs[i]]);
		}

		this.client.upsertEntities(this.Entity, tmpRecordsToUpsert,
			(pError, pBody) =>
			{
				if (pError)
				{
					this.log.error(`Error sending PUT [${this.Entity}] to server:  ${pError}`);
					return this.upsertBulkRecords(fCallback, pRecordGUIDs, tmpRetryCount + 1);
				}

				if (Array.isArray(pBody))
				{
					this.reconcileBulkUpsertResponse(pRecordGUIDs, pBody);
					return fCallback();
				}

				// Try again...
				this.log.error(`Problem sending PUT [${this.Entity}] to server.  Incrementing retry count and trying again.`, { Records: pBody, RetryCount: tmpRetryCount });
				this.upsertBulkRecords(fCallback, pRecordGUIDs, tmpRetryCount + 1);
			});
	}

	/**
	 * Increment the meta progress tracker (if configured) and conditionally log its status.
	 *
	 * @param {number} pAmount - The number of operations to increment by
	 */
	_incrementMetaProgressTracker(pAmount)
	{
		if (!this.MetaProgressTrackerHash)
		{
			return;
		}
		let tmpTracker = this.fable.ProgressTrackerSet;
		let tmpStatus = tmpTracker.incrementProgressTracker(this.MetaProgressTrackerHash, pAmount);

		if (this.MetaProgressTrackerLogInterval > 0 && tmpStatus)
		{
			// Use a threshold-crossing check rather than exact modulo so that
			// bulk increments (e.g. +100) reliably trigger a log when they cross
			// an interval boundary.  This makes the interval mean "log at least
			// every N records" regardless of whether records are pushed one at a
			// time or in batches.
			let tmpPreviousCount = tmpStatus.CurrentCount - pAmount;
			let tmpPreviousInterval = Math.floor(tmpPreviousCount / this.MetaProgressTrackerLogInterval);
			let tmpCurrentInterval = Math.floor(tmpStatus.CurrentCount / this.MetaProgressTrackerLogInterval);

			if (tmpCurrentInterval > tmpPreviousInterval
				|| tmpStatus.CurrentCount >= tmpStatus.TotalCount)
			{
				tmpTracker.logProgressTrackerStatus(this.MetaProgressTrackerHash);
			}
		}
	}

	/**
	 * Push any records in the adapter buffer to the server
	 *
	 * @param {(error?: Error) => void} fCallback - Callback function to call when the records are pushed
	 */
	pushRecordsToServer(fCallback)
	{
		if (!this._PerformUpserts)
		{
			return fCallback();
		}
		// Run the upserts...
		let tmpRecordKeys = Object.keys(this._MarshaledRecords);
		// Nothing to upsert is a legitimate outcome, not a problem: a retirement-only load routes
		// every record to the delete queue. Falling through would build a progress tracker with a
		// total of 0, which logs at ERROR level and flips an otherwise clean run to
		// `succeeded-with-failures` -- the one status a caller must not learn to ignore.
		if (tmpRecordKeys.length < 1)
		{
			return fCallback();
		}
		let tmpAnticipate = this.fable.instantiateServiceProviderWithoutRegistration('Anticipate');
		let tmpProgressTrackerGUID = this.fable.getUUID();
		let tmpProgressTracker = this.fable.ProgressTrackerSet;
		tmpProgressTracker.createProgressTracker(tmpProgressTrackerGUID, tmpRecordKeys.length);
		tmpProgressTracker.startProgressTracker(tmpProgressTrackerGUID);

		// Log meta tracker at the start of each entity
		if (this.MetaProgressTrackerHash)
		{
			this.log.info(`[${this.Entity}] Starting push of ${tmpRecordKeys.length} records...`);
			tmpProgressTracker.logProgressTrackerStatus(this.MetaProgressTrackerHash);
		}

		// Determine if bulk upserts are possible
		if (tmpRecordKeys.length > this.options.RecordThresholdForBulkUpsert)
		{
			let tmpBulkUpsertBatchCount = Math.ceil(tmpRecordKeys.length / this.options.BulkUpsertBatchSize);
			for (let i = 0; i < tmpBulkUpsertBatchCount; i++)
			{
				let tmpBatchRecordKeys = [];
				for (let j = 0; j < this.options.BulkUpsertBatchSize; j++)
				{
					let tmpRecordKey = tmpRecordKeys[(i * this.options.BulkUpsertBatchSize) + j];
					if (tmpRecordKey)
					{
						tmpBatchRecordKeys.push(tmpRecordKey);
					}
				}

				tmpAnticipate.anticipate(
					function(fDone)
					{
						this.log.trace(`[${this.Entity}] Bulk Upserting ${tmpBatchRecordKeys.length} records to server for transaction set [${tmpProgressTrackerGUID}]...`);
						this.upsertBulkRecords(
							(pError, pBody)=>
							{
								if (pError)
								{
									this.log.error(`Error sending Bulk Upserts for [${this.Entity}] to server:  ${pError}`);
								}
								tmpProgressTracker.incrementProgressTracker(tmpProgressTrackerGUID, tmpBatchRecordKeys.length);
								tmpProgressTracker.logProgressTrackerStatus(tmpProgressTrackerGUID);
								this._incrementMetaProgressTracker(tmpBatchRecordKeys.length);
								return fDone();
							}, tmpBatchRecordKeys);
					}.bind(this));
			}
		}
		else
		{
			for (let i = 0; i < tmpRecordKeys.length; i++)
			{
				let tmpRecordKey = tmpRecordKeys[i];
				tmpAnticipate.anticipate(
					function(fDone)
					{
						this.log.trace(`[${this.Entity}] Record [${tmpRecordKey}] pushing to server...`);
						tmpProgressTracker.incrementProgressTracker(tmpProgressTrackerGUID, 1);
						tmpProgressTracker.logProgressTrackerStatus(tmpProgressTrackerGUID);
						this.upsertSingleRecord(
							()=>
							{
								this._incrementMetaProgressTracker(1);
								return fDone();
							}, tmpRecordKey);
					}.bind(this));
			}
		}

		tmpAnticipate.wait(fCallback);
	}

	/**
	 * Delete record from the adapter's marshaled records buffer
	 *
	 * @param {(error?: Error) => void} fCallback - Callback function to call when the records have been deleted
	 */
	deleteRecordsFromServer(fCallback)
	{
		if (!this._PerformDeletes)
		{
			return fCallback();
		}
		// Run the deletes...
		// TODO: THIS IS DANGEROUS
		let tmpRecordKeys = Object.keys(this._DeletedRecords);
		if (tmpRecordKeys.length < 1)
		{
			return fCallback();
		}

		const fDeleteRecords = () =>
		{
			libAsync.eachSeries(tmpRecordKeys,
				(pRecordGUID, fDeleteComplete) =>
				{
					this.log.trace(`[${this.Entity}] Record [${this._MarshaledRecords[this.EntityGUIDName]}] deleting from server...`);
					const tmpIDRecord = this.fable.HeadlightGUIDMap.getIDFromGUID(this.Entity, pRecordGUID);
					if (tmpIDRecord)
					{
						return this.client.deleteEntity(this.Entity, tmpIDRecord, fDeleteComplete);
					}

					this.log.warn(`Could not delete [${this.Entity}] GUID [${pRecordGUID}] because lookup on HeadLight did not return an IDRecord.`);
					return fDeleteComplete();
				},
				(pError)=>
				{
					if (pError)
					{
						this.log.error(`Error sending trying to Delete from [${this.Entity}]:  ${pError}`);
					}
					return fCallback(pError);
				});
		};

		if (!this.options.BatchGUIDResolution)
		{
			libAsync.eachSeries(tmpRecordKeys,
				(pRecordGUID, fLookupComplete) =>
				{
					this.client.getEntityByGUID(this.Entity, pRecordGUID,
						(pReadError, pReadBody)=>
						{
							if (pReadError)
							{
								this.log.warn(`Could not read [${this.Entity}] GUID [${pRecordGUID}] for DELETE operation:  ${pReadError}`);
								return fLookupComplete();
							}
							if (pReadBody && pReadBody.hasOwnProperty(this.EntityIDName))
							{
								this.fable.HeadlightGUIDMap.mapGUIDToID(this.Entity, pRecordGUID, pReadBody[this.EntityIDName]);
							}
							return fLookupComplete();
						});
				},
				() => fDeleteRecords());
			return;
		}

		this.guidBatchResolver.resolveEntityGUIDs(this.Entity, tmpRecordKeys)
			.then(() => fDeleteRecords())
			.catch((pError) =>
			{
				this.log.warn(`Could not resolve [${this.Entity}] records to delete in batches: ${pError.message || pError}`);
				return fDeleteRecords();
			});
	}
}

module.exports = HeadlightIntegrationAdapter;

// Macro for backwards compatibility
module.exports.getAdapter = (
	/**
	 * @param {import('fable')} pFable
	 * @param {string} pEntity
	 * @param {string} pEntityPrefix
	 * @param {Object} [pCustomOptions]
	 */
	function(pFable, pEntity, pEntityPrefix, pCustomOptions)
	{
		if (pFable.servicesMap.IntegrationAdapter && pFable.servicesMap.IntegrationAdapter.hasOwnProperty(pEntity))
		{
			pFable.log.info(`Reusing existing Integration Adapter for entity [${pEntity}].`);
			return pFable.servicesMap.IntegrationAdapter[pEntity];
		}
		else
		{
			const tmpOptions = Object.assign({}, pCustomOptions, { Entity: pEntity, EntityGUIDMarshalPrefix: pEntityPrefix });
			return pFable.instantiateServiceProvider('IntegrationAdapter', tmpOptions, pEntity);
		}
	});

module.exports.default_configuration = defaultHeadlightIntegrationAdapterOptions;
module.exports.GUID_MAX_LENGTH_FALLBACK = _GUID_MAX_LENGTH_FALLBACK;
module.exports.GUIDBatchResolver = libGUIDBatchResolver;

// Exported for testing: the predicate that decides whether a source record is a delete.
module.exports.isDeletedFlagSet = isDeletedFlagSet;

// Exported for testing: the normalizer for the per-record `Error` property on a bulk response row.
module.exports.getRecordErrorMessage = getRecordErrorMessage;

/**
 * Report every record the server refused across all of a run's adapters.
 *
 * The bulk endpoints answer 200 for a batch in which individual rows failed, so these never reach
 * a run-level error callback. They are terminal for the record and not retried, which makes an
 * end-of-run tally the only place a partial load is visible.
 *
 * @param {import('fable')} pFable - the fable the adapters were registered on
 * @return {number} how many records were rejected across every entity
 */
module.exports.logRecordPushFailureSummary = function(pFable)
{
	let tmpAdapters = pFable.servicesMap.IntegrationAdapter;

	if (!tmpAdapters)
	{
		return 0;
	}

	let tmpFailures = [];
	let tmpFailureCountByEntity = {};
	let tmpAdapterKeys = Object.keys(tmpAdapters);

	for (let i = 0; i < tmpAdapterKeys.length; i++)
	{
		let tmpAdapter = tmpAdapters[tmpAdapterKeys[i]];

		if (!tmpAdapter || !Array.isArray(tmpAdapter.RecordPushFailures) || (tmpAdapter.RecordPushFailures.length < 1))
		{
			continue;
		}

		tmpFailureCountByEntity[tmpAdapter.Entity] = tmpAdapter.RecordPushFailures.length;
		tmpFailures = tmpFailures.concat(tmpAdapter.RecordPushFailures);
	}

	if (tmpFailures.length < 1)
	{
		pFable.log.info(`No records were rejected by the server during this load.`);
		return 0;
	}

	pFable.log.error(`${tmpFailures.length} record(s) were rejected by the server during this load and were NOT persisted:`);

	let tmpEntityKeys = Object.keys(tmpFailureCountByEntity);
	for (let i = 0; i < tmpEntityKeys.length; i++)
	{
		pFable.log.error(`  [${tmpEntityKeys[i]}]: ${tmpFailureCountByEntity[tmpEntityKeys[i]]} rejected record(s)`);
	}

	for (let i = 0; i < tmpFailures.length; i++)
	{
		pFable.log.error(`  [${tmpFailures[i].Entity}].[${tmpFailures[i].RecordKey}]: ${tmpFailures[i].Error}`);
	}

	return tmpFailures.length;
};
